import { useState, useCallback } from "react";
import { Box } from "@mui/material";
import { useTranslationApp } from "../../../../lib/i18next";
import { useDispatchApp } from "../../../../lib/redux";
import {
  BUTTON_DOWNLOAD,
  BUTTON_SAVE,
  MODAL_IMAGE_EDIT_SAVE_ALERT_DESCRIPTION,
  MODAL_IMAGE_EDIT_REPLACE_BUTTON,
  MODAL_IMAGE_EDIT_SAVE_ALERT_TITLE,
  MODAL_IMAGE_EDIT_SAVE_AS_NEW_BUTTON,
} from "../../../../i18n/keysTranslations";
import { startGetAwsDownloadUrl } from "../../../../actions/user";
import { ConfirmAlert } from "../../../Alerts/Alerts";
import Button from "../../../Buttons/Button";

import { LaunchError } from "../../../../utils/errors";

const SaveContentActions = ({
  disabled = false,
  showSaveAlert = true,
  showDownload = false,
  downloadUrl = null,
  contentUrl = null,
  onSave = () => {},
  onCloseAfterSave,
  variant = "footer",
  saveAlertTitleKey = MODAL_IMAGE_EDIT_SAVE_ALERT_TITLE,
  saveAlertDescriptionKey = MODAL_IMAGE_EDIT_SAVE_ALERT_DESCRIPTION,
  replaceButtonKey = MODAL_IMAGE_EDIT_REPLACE_BUTTON,
  saveAsNewButtonKey = MODAL_IMAGE_EDIT_SAVE_AS_NEW_BUTTON,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const buildSavePayload = useCallback(
    (mode) => {
      const payload = { mode };
      if (contentUrl) {
        payload.url = contentUrl;
      }
      return payload;
    },
    [contentUrl]
  );

  const onDownload = useCallback(
    async (event) => {
      event?.stopPropagation?.();
      if (!downloadUrl) return;

      try {
        setIsDownloading(true);
        await dispatch(startGetAwsDownloadUrl(downloadUrl));
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "SaveContentActions",
            capture: "exception",
          })
        );
        console.error("Error downloading image:", error);
      } finally {
        setIsDownloading(false);
      }
    },
    [dispatch, downloadUrl]
  );

  const onSaveContent = useCallback(
    async (event) => {
      event?.stopPropagation?.();
      if (disabled) return;

      try {
        setIsSaving(true);

        if (!showSaveAlert) {
          await onSave(buildSavePayload("replace"));
          onCloseAfterSave?.();
          return;
        }

        const response = await ConfirmAlert({
          title: t(saveAlertTitleKey),
          text: t(saveAlertDescriptionKey),
          showCancelButton: true,
          showThirdButton: true,
          confirmButtonText: t(replaceButtonKey),
          thirdButtonText: t(saveAsNewButtonKey),
          confirmButtonVariant: "secondary",
          cancelButtonVariant: "secondary",
          denyButtonVariant: "secondary",
          width: "550px",
        });

        if (response?.isConfirmed) {
          await onSave(buildSavePayload("replace"));
        } else if (response?.isDenied) {
          await onSave(buildSavePayload("new"));
        } else {
          return;
        }

        onCloseAfterSave?.();
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "SaveContentActions",
            capture: "exception",
          })
        );
        console.error("Error saving content:", error);
      } finally {
        setIsSaving(false);
      }
    },
    [
      buildSavePayload,
      disabled,
      dispatch,
      onCloseAfterSave,
      onSave,
      replaceButtonKey,
      saveAlertDescriptionKey,
      saveAlertTitleKey,
      saveAsNewButtonKey,
      showSaveAlert,
      t,
    ]
  );

  const isCompact = variant === "compact";

  return (
    <Box
      sx={{
        display: "flex",
        gap: isCompact ? 1.5 : 2,
        justifyContent: isCompact ? "flex-end" : "center",
        alignItems: "center",
        p: isCompact ? 0 : 4,
        borderTop: isCompact ? 0 : "1px solid",
        borderColor: "divider",
        bgcolor: isCompact ? "transparent" : "background.paper",
        flexShrink: 0,
      }}
    >
      {showDownload && downloadUrl ? (
        <Button
          color="secondary"
          onClick={onDownload}
          disabled={isDownloading}
          loading={isDownloading}
          sx={{ minWidth: isCompact ? 120 : 140 }}
        >
          {t(BUTTON_DOWNLOAD)}
        </Button>
      ) : null}

      <Button
        variant="contained"
        color="primary"
        onClick={onSaveContent}
        disabled={disabled || isSaving}
        loading={isSaving}
        sx={{ minWidth: isCompact ? 120 : 180 }}
      >
        {t(BUTTON_SAVE)}
      </Button>
    </Box>
  );
};

export default SaveContentActions;
